import React, { memo, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getArtistTopTracks } from '../features/artists/services/tracks.api';
import { SpotifyTrack } from '@/types';

interface ArtistTopTracksProps {
  artistId: string;
}

const ArtistTopTracks: React.FC<ArtistTopTracksProps> = memo(({ artistId }) => {
  const { data: tracks, isLoading, isError } = useQuery<SpotifyTrack[], Error>({
    queryKey: ['artistTopTracks', artistId],
    queryFn: () => getArtistTopTracks(artistId),
    enabled: !!artistId,
  });

  if (isLoading) {
    return <div className="text-white">Carregando faixas...</div>;
  }

  if (isError) {
    return <div className="text-red-500">Erro ao carregar faixas.</div>;
  }

  if (!tracks || tracks.length === 0) {
    return <div className="text-white">Nenhuma faixa popular encontrada.</div>;
  }

  return (
    <div className="bg-zinc-800 p-4 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold text-white mb-4">Faixas Mais Populares</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {useMemo(() => tracks.map((track) => (
          <div key={track.id} className="bg-zinc-700 p-3 rounded-md flex items-center space-x-3">
            <img
              src={track.album.images[0]?.url || 'https://via.placeholder.com/64'}
              alt={track.album.name}
              className="w-16 h-16 rounded-md object-cover"
            />
            <div className="flex-1">
              <h3 className="text-white font-semibold">{track.name}</h3>
              <p className="text-gray-400 text-sm">{track.artists.map((artist) => artist.name).join(', ')}</p>
              <p className="text-gray-500 text-xs">Popularidade: {track.popularity}</p>
            </div>
            {track.preview_url && (
              <audio controls src={track.preview_url} className="w-24 h-8">
                Seu navegador não suporta o elemento de áudio.
              </audio>
            )}
          </div>
        )), [tracks])}
      </div>
    </div>
  );
});

export default ArtistTopTracks;
